import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaCalendarAlt } from "react-icons/fa";

const formatTanggal = (dateStr) =>
  new Date(dateStr).toLocaleDateString("id-ID", {
    day: "numeric", month: "long", year: "numeric",
  });

const truncateText = (text, max = 100) => {
  if (!text) return "-";
  // Strip HTML tags dari deskripsi quill
  const plain = text.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return plain.length <= max ? plain : plain.substring(0, max) + "…";
};

const KATEGORI_COLOR = {
  "News":       { bg: "#0f766e", text: "#fff" },
  "Case Study": { bg: "#1a4731", text: "#fff" },
  "Article":    { bg: "#7c3aed", text: "#fff" },
};

const StoriesComponent = ({
  stories,
  onDeleteClick,
  accentColor,
  isDark,
  cardBg,
  borderColor,
  textPrimary,
  textSecondary,
}) => {
  const navigate = useNavigate();

  if (stories.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {stories.map((story, index) => {
        const katColor = KATEGORI_COLOR[story.kategori] || { bg: accentColor, text: "#fff" };
        return (
          <motion.div
            key={story.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.06 }}
            className="rounded-xl border overflow-hidden flex flex-col group"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            {/* Image */}
            <div className="relative overflow-hidden" style={{ height: "180px" }}>
              {story.url ? (
                <img
                  src={story.url}
                  alt={story.judul}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.style.backgroundColor = `${accentColor}15`;
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}15` }}>
                  <span className="text-xs" style={{ color: textSecondary }}>No Image</span>
                </div>
              )}

              {/* Gradient overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to top, ${isDark ? "rgba(26,26,24,0.7)" : "rgba(0,0,0,0.25)"} 0%, transparent 60%)`,
                }}
              />

              {/* Kategori badge */}
              <div
                className="absolute top-3 left-3 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: katColor.bg, color: katColor.text }}
              >
                {story.kategori}
              </div>

              {/* Action buttons on hover */}
              <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
                <button
                  onClick={() => navigate(`/stories/edit/${story.id}`)}
                  className="p-2 rounded-lg shadow-md backdrop-blur-sm transition-all duration-200 hover:scale-110"
                  style={{ backgroundColor: accentColor, color: "#fff" }}
                  title="Edit stories"
                >
                  <FaEdit className="text-xs" />
                </button>
                <button
                  onClick={() => onDeleteClick(story)}
                  className="p-2 rounded-lg shadow-md backdrop-blur-sm transition-all duration-200 hover:scale-110"
                  style={{ backgroundColor: "#dc2626", color: "#fff" }}
                  title="Hapus stories"
                >
                  <FaTrash className="text-xs" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-4">
              {/* Tanggal */}
              <div className="flex items-center gap-1.5 mb-2">
                <FaCalendarAlt className="text-[10px]" style={{ color: accentColor }} />
                <span className="text-[11px]" style={{ color: accentColor }}>
                  {formatTanggal(story.createdAt)}
                </span>
              </div>

              <h3 className="font-serif font-bold text-sm leading-snug mb-2 line-clamp-2" style={{ color: textPrimary }}>
                {story.judul || "-"}
              </h3>

              <div className="w-8 h-0.5 mb-3 rounded-full" style={{ backgroundColor: accentColor }} />

              <p className="text-xs leading-relaxed flex-1 mb-4" style={{ color: textSecondary }}>
                {truncateText(story.deskripsi)}
              </p>

              {/* Mobile actions */}
              <div className="flex md:hidden items-center justify-end pt-3 border-t" style={{ borderColor }}>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/stories/edit/${story.id}`)}
                    className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                    style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                  >
                    <FaEdit className="text-sm" />
                  </button>
                  <button
                    onClick={() => onDeleteClick(story)}
                    className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                    style={{ backgroundColor: "#fef2f2", color: "#dc2626" }}
                  >
                    <FaTrash className="text-sm" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default StoriesComponent;